// ── Jobs Library ──────────────────────────────────────────────────────────────

// Fields to search in requisition-number view
export const REQ_SEARCHABLE_FIELDS = [
  "sb_requisition_number",
  "job_code",
  "job_title_for_jd",
  "legal_entity",
  "business_unit",
  "role_type",
  "location",
  "function",
  "sub_function",
  "job_level",
  "version_info",
];

// Fields to search in job-code view (parent-level keys only)
export const JOB_CODE_SEARCHABLE_FIELDS = [
  "job_code",
  "job_title",
  "business_unit",
  "role_type",
  "job_level",
  "legal_entity",
];

export const FILTER_KEYS = [
  "sb_requisition_number",
  "job_code",
  "job_title",
  "legal_entity",
  "business_unit",
  "role_type",
  "location",
  "function",
  "sub_function",
  "job_level",
  "version_info",
];

export const VIEW_MODES = {
  JOB_CODE:          'job-code',
  REQUISITION_NUMBER: 'requisition-number',
};

// ── JD Status ─────────────────────────────────────────────────────────────────

export const JD_STATUSES = {
  PENDING_ANALYSIS: 'PENDING_ANALYSIS',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  PUBLISHED:        'PUBLISHED',
  UNPUBLISHED:      'UNPUBLISHED',
  NEEDS_REVISION:   'NEEDS_REVISION',
  GENERATED:        'GENERATED',
  REGENERATED:      'REGENERATED',
  LOCKED:           'LOCKED',
  NEEDS_WORK:       'NEEDS_WORK',
  GENERATION_FAILED: 'GENERATION_FAILED',
};

// Statuses that allow editing a JD
export const JD_STATUSES_EDITABLE = [
  JD_STATUSES.GENERATED,
  JD_STATUSES.REGENERATED,
  JD_STATUSES.UNPUBLISHED,
];

// PRD §5.1 — tooltip content strings for a JD's status. Shared by Jobs Library
// and Compensation Management so both surfaces describe each status identically.
export const TOOLTIP_STATUS_PROCESSING = "This JD is currently being generated by AI. This typically takes 4-5 minutes. You cannot open it until generation is complete.";
export const TOOLTIP_STATUS_UNPUBLISHED = "This JD has been generated but not yet published. It can be edited, revised, or sent for approval.";
export const TOOLTIP_STATUS_PENDING_APPROVAL = "This JD has been submitted for review. The assigned approver will review and either publish or request revisions.";
export const TOOLTIP_STATUS_NEEDS_REVISION = "The approver has requested changes to this JD. The creator needs to make the requested edits and resubmit.";
export const TOOLTIP_STATUS_PUBLISHED = "This JD is live and available for use across SkillsBridge. Download as PDF or DOCX from the Job Details page.";
export const TOOLTIP_STATUS_PROCESSING_FAILED = "JD generation failed. Use the Retry button to try again, or contact your administrator if the issue persists.";

// Maps an underlying jd_status to the appropriate PRD tooltip content.
export const getStatusTooltip = (status) => {
  switch (status) {
    case JD_STATUSES.PUBLISHED:
      return TOOLTIP_STATUS_PUBLISHED;
    case JD_STATUSES.PENDING_APPROVAL:
      return TOOLTIP_STATUS_PENDING_APPROVAL;
    case JD_STATUSES.NEEDS_REVISION:
      return TOOLTIP_STATUS_NEEDS_REVISION;
    case JD_STATUSES.PENDING_ANALYSIS:
      return TOOLTIP_STATUS_PROCESSING;
    case JD_STATUSES.GENERATION_FAILED:
      return TOOLTIP_STATUS_PROCESSING_FAILED;
    case JD_STATUSES.UNPUBLISHED:
    default:
      return TOOLTIP_STATUS_UNPUBLISHED;
  }
};

// Base status badge display (label/color/tooltip) shared by Jobs Library and
// Compensation Management. Jobs Library layers its own live-task-tracking
// override on top of this (e.g. "Processing Failed" while a JD is generating)
// -- a case Compensation Management's data never includes, since its backing
// query already excludes PENDING_ANALYSIS/GENERATION_FAILED rows.
export const getJdStatusBadge = (status) => ({
  label: status === JD_STATUSES.PUBLISHED ? 'Published' : 'Unpublished',
  color: status === JD_STATUSES.PUBLISHED ? 'success' : 'processing',
  tooltip: getStatusTooltip(status),
});

// Status column filter (Ant Table `filters`/`onFilter`) shared by Jobs Library
// and Compensation Management: "Published" matches PUBLISHED exactly;
// "Unpublished" buckets every other jd_status together (GENERATED,
// PENDING_APPROVAL, UNPUBLISHED, HUMAN-EDITED, etc.).
export const JD_STATUS_FILTERS = [
  { text: 'Published', value: JD_STATUSES.PUBLISHED },
  { text: 'Unpublished', value: JD_STATUSES.UNPUBLISHED },
];

export const jdStatusOnFilter = (value, record) =>
  value === JD_STATUSES.PUBLISHED
    ? record.jd_status === JD_STATUSES.PUBLISHED
    : record.jd_status !== JD_STATUSES.PUBLISHED;

// ── JD Tabs ───────────────────────────────────────────────────────────────────

export const JD_TABS = {
  JOB_DESCRIPTION: 'job-description',
  JOB_COMPARE:     'job-compare',
};

export const JD_TAB_LABELS = {
  [JD_TABS.JOB_DESCRIPTION]: 'Job Description',
  [JD_TABS.JOB_COMPARE]:     'Compare Published JDs',
};

// ── Human Editor Block Types ───────────────────────────────────────────────────

export const BLOCK_TYPES = {
  LOCKED:     'LOCKED',
  NEEDS_WORK: 'NEEDS_WORK',
  GHOST:      'GHOST',
  MODIFIED:   'MODIFIED',
};

// ── Compare View ──────────────────────────────────────────────────────────────

export const COMPARE_VIEW = {
  DIFFERENCE: 'difference',
  SUMMARY:    'summary',
};

export const COMPARE_VIEW_LABELS = {
  [COMPARE_VIEW.DIFFERENCE]: 'Side-by-side view',
  [COMPARE_VIEW.SUMMARY]:    'AI Comparison Summary',
};

export const COMPARE_ENTRY = {
  JOBS_LIBRARY: 'jobs-library',
  JOB_DETAILS:  'job-details',
};

export const COMPARE_LOADING = {
  AI:      'ai',
  SUMMARY: 'summary',
};

// ── Diff Checker ──────────────────────────────────────────────────────────────

export const VIEW_MODE = {
  LOADING:  'LOADING',
  APPROVER: 'APPROVER',
  HISTORY:  'HISTORY',
};

export const WORKFLOW_STATUS = {
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  LOADING_STATUS:   'LOADING_STATUS',
};

export const DECISION = {
  APPROVE:  'APPROVE',
  REVIEW:   'REVIEW',
  REMOVED:  'REMOVED',
  MODIFIED: 'MODIFIED',
  UNCHANGED:'UNCHANGED'
};

export const STATUS_CLASS_MAP = {
  added:       'diff-added',
  removed:     'diff-removed',
  modified:    'diff-modified',
  ghost_alert: 'diff-ghost-alert',
};


// ── Feedback ──────────────────────────────────────────────────────────────────

export const FEEDBACK_TYPE = {
    UP: 'WORKS',
    DOWN: 'HAS_ISSUES',
};

// ── JD Creation ───────────────────────────────────────────────────────────────

export const JD_ACTION = { CREATE: 'create', REVISE: 'revise', UPLOAD: 'upload' };

export const FIELD_NAME_MAP = {
  jobCode:            'job_code',
  roleTitle:          'role',
  altRoleTitle:       'alt_role_title',
  jobTitleForJd:      'job_title_for_jd',
  legalEntity:        'legal_entity',
  businessUnit:       'business_unit',
  functionDepartment: 'function',
  subFunction:        'sub_function',
  reportingTo:        'reporting_to',
  location:           'location',
  jobGrade:           'level',
  roleType:           'role_type',
  workEnvironment:    'work_environment',
  roleContext:        'role_context',
};

export const FIELD_DB_MAP = {
  legalEntity:        'legal_entity',
  businessUnit:       'business_unit',
  functionDepartment: 'function',
  jobGrade:           'level',
  roleType:           'role_type',
  subFunction:        'sub_function',
  location:           'location',
  workEnvironment:    'work_environment',
};
